import { UTM_PARAM_IDS } from '$lib/global/helper/get-query-parameter';
import { getAllTrackingProperties, getDefaultTrackingProperties } from '$lib/helper';

import type { EmbedContent } from '$lib/types/Components/Embed/EmbedContent';
import type { TrackingProperty } from '$lib/types/TrackingProperty';

// AI embeds default to non-sensitive (no overlay); users who do embed third-party
// tools via the AI block can opt in by setting isGdprSensitive: true. All other
// providers default to sensitive for backward compatibility.
export const resolveIsGdprSensitive = (content: EmbedContent): boolean =>
    content.isGdprSensitive ?? content.provider !== 'ai';

export const hasExternalContentOverlay = ({
    content,
    cookiesEnabled,
    hasAcceptedMediaOverlay,
}: {
    content: EmbedContent;
    cookiesEnabled: boolean;
    hasAcceptedMediaOverlay: boolean;
}): boolean => {
    const isGdprSensitive = resolveIsGdprSensitive(content);

    // Custom HTML and AI embeds that are not GDPR sensitive do not require an overlay
    if ((content.provider === 'custom-html' || content.provider === 'ai') && !isGdprSensitive) {
        return false;
    }

    // For all other embeds if cookies are not accepted yet, we show the overlay
    return !!(
        cookiesEnabled &&
        !hasAcceptedMediaOverlay &&
        (content.head || content.body || content.src)
    );
};

// Mapping of common short field names to their address-prefixed equivalents.
// When a fullAddress input is used, address components are stored with the
// "address." prefix (e.g., address.postalCode). This allows users to use
// short names like {{zip}} in embed blocks, falling back to the prefixed value.
const ADDRESS_FIELD_ALIASES: Record<string, string> = {
    zip: 'address.postalCode',
    postal: 'address.postalCode',
    postalCode: 'address.postalCode',
    city: 'address.city',
    street: 'address.street',
    country: 'address.country',
};

export const injectHtml = (html: string, targetNode: HTMLElement) => {
    const helper = document.createElement('div');
    helper.innerHTML = html;

    // We need to replace all script nodes in order for the browser to execute them.
    // script tags being placed in the DOM by setting innerHTML won't be executed.
    // script tags that are created with createElement and that are attached with
    // appendChild on the other hand are executed by the browser.
    const originalScriptNodes = helper.querySelectorAll('script');

    for (const originalScriptNode of originalScriptNodes) {
        const replacementScriptNode = document.createElement('script');

        for (const attribute of originalScriptNode.attributes) {
            const attributeValue = attribute.value;

            if (attributeValue && attributeValue.length > 0) {
                replacementScriptNode.setAttribute(attribute.name, attributeValue);
            }
        }

        replacementScriptNode.text = originalScriptNode.innerHTML;

        originalScriptNode.parentNode?.replaceChild(replacementScriptNode, originalScriptNode);
    }

    targetNode.append(...Array.from(helper.childNodes));
};

export const createUniqueCrossOriginIframeSandboxUrl = (uniqueIdentifier: string): URL => {
    return new URL(`https://${uniqueIdentifier}-funnel-app.perspective-sandbox.co/funnel-app.html`);
};

export const interpolateHeadAndBody = (
    trackingProperties: TrackingProperty[],
    globalVariables: Record<string, string>,
    campaignId: string,
    scripts: { head: string; body: string },
): { head: string; body: string } => {
    const defaultTrackingProperties = getDefaultTrackingProperties(trackingProperties);
    const savedTrackingProperties = getAllTrackingProperties(campaignId);
    const values = {
        ...defaultTrackingProperties,
        ...savedTrackingProperties,
        ...globalVariables,
    };

    let interpolatedHead = scripts.head ?? '';
    let interpolatedBody = scripts.body ?? '';

    const allVariableNames: Set<string> = new Set([
        // we interpolate all supported UTM parameters,
        // if some parameter is not defined, it will be replaced
        // by the fallback '' (empty string)
        ...UTM_PARAM_IDS,
        ...Object.keys(defaultTrackingProperties),
        ...Object.keys(savedTrackingProperties),
        ...Object.keys(globalVariables),
    ]);

    // Resolve address field aliases: if a short name (e.g., "zip") has no value,
    // fall back to the address-prefixed equivalent (e.g., "address.postalCode")
    for (const [shortName, addressFieldName] of Object.entries(ADDRESS_FIELD_ALIASES)) {
        if (!values[shortName] && values[addressFieldName]) {
            values[shortName] = values[addressFieldName];
            allVariableNames.add(shortName);
        }
    }

    for (const variableName of allVariableNames) {
        const variableValue = values[variableName] ?? '';

        const regex = new RegExp(`\\{\\{\\s*${variableName}\\s*\\}\\}`, 'g');

        interpolatedHead = interpolatedHead.replace(regex, variableValue);
        interpolatedBody = interpolatedBody.replace(regex, variableValue);
    }

    return {
        head: interpolatedHead,
        body: interpolatedBody,
    };
};
